import { TV2M_EVENT_BATCH_MAX } from '@apm/contracts';
/** 전환 이벤트 수집 경로 (`docs/08` §3). BLOOM Server 의 `/v1/events/batch` 에 대응한다. */
export const TV2M_EVENTS_BULK_PATH = '/v1/events/bulk';
/**
 * 전환 이벤트 큐. `packages/bloom-receiver-sdk` 의 `EventQueue` 와 **같은 규약**이다
 * (`docs/09` T6 — "packages/bloom-receiver-sdk 것과 동일 규약, never throws, 배치 50").
 *
 * **이 클래스의 어떤 메서드도 throw 하지 않는다.** 이벤트 수집이 전환을 방해하면 안 된다
 * (`docs/08` §6). 시청자는 상품 화면을 보러 온 것이지 우리 계측을 도우러 온 것이 아니다.
 *
 * 상한은 `@apm/contracts` 의 `TV2M_EVENT_BATCH_MAX`(50). 서버 상한과 같은 상수를 쓴다 —
 * 두 곳에 숫자를 두면 큐가 서버가 거부하는 크기를 보낸다. 가득 차면 **오래된 것부터 버린다.**
 *
 * **타이머를 쓰지 않는다.** `setInterval` 은 코어가 참조할 수 없는 플랫폼 API 다
 * (`lib: ES2022` 에 없다). 재시도는 다음 `enqueue`/`flush` 가 태운다.
 *
 * `packages/bloom-receiver-sdk` 와 다른 점이 하나 있다: **단건 엔드포인트가 없다.** TV2M 계약에는
 * `/v1/events/bulk` 하나뿐이라 한 건이어도 배열로 보낸다 (`docs/08` §3 의 표).
 */
export class Tv2mEventQueue {
    network;
    queue = [];
    sending = false;
    dropped = 0;
    baseUrl;
    constructor(network, serverUrl) {
        this.network = network;
        this.baseUrl = serverUrl.replace(/\/+$/, '');
    }
    /** 큐에 넣고 전송을 시도한다. 결과를 기다리지 않는다. */
    enqueue(event) {
        if (this.queue.length >= TV2M_EVENT_BATCH_MAX) {
            this.queue.shift();
            this.dropped += 1;
        }
        this.queue.push(event);
        void this.flush();
    }
    /**
     * 큐를 비운다. 실패하면 보낸 것을 큐 앞으로 되돌려 다음 기회에 다시 시도한다.
     * **절대 reject 하지 않는다.**
     *
     * **비울 때까지 돈다.** 전송 중에 들어온 이벤트를 남겨두면 `sending` 가드에 걸려
     * 아무도 그것을 태우지 않는다 — 실패가 없어도 가장 최근 이벤트가 계속 한 박자 밀린다
     * (`packages/bloom-receiver-sdk` 의 같은 클래스가 테스트 작성 중에 실제로 걸렸던 자리다).
     * 실패했을 때만 루프를 빠져나가 다음 기회로 넘긴다 (그래야 오프라인에서 회전하지 않는다).
     */
    async flush() {
        if (this.sending || this.queue.length === 0)
            return;
        this.sending = true;
        try {
            while (this.queue.length > 0) {
                const batch = this.queue.splice(0, TV2M_EVENT_BATCH_MAX);
                try {
                    await this.network.post(`${this.baseUrl}${TV2M_EVENTS_BULK_PATH}`, batch);
                }
                catch {
                    this.requeue(batch);
                    break;
                }
            }
        }
        finally {
            this.sending = false;
        }
    }
    /** 실패한 배치를 앞으로 되돌린다. 되돌린 뒤 넘치면 오래된 것부터 버린다. */
    requeue(batch) {
        this.queue = [...batch, ...this.queue];
        if (this.queue.length > TV2M_EVENT_BATCH_MAX) {
            this.dropped += this.queue.length - TV2M_EVENT_BATCH_MAX;
            this.queue = this.queue.slice(this.queue.length - TV2M_EVENT_BATCH_MAX);
        }
    }
    /** 진단용. 디버그 화면이 "이벤트가 새고 있는가" 를 보는 유일한 창이다. */
    get stats() {
        return { pending: this.queue.length, dropped: this.dropped };
    }
}
//# sourceMappingURL=event-queue.js.map